import React, { useState } from 'react';
import { omit } from 'lodash';
import { styled } from '@mui/material';

import { Button, Checkbox, Dropdown, IconButton, SplitButton, Stack } from 'shell/components';

const DrawerActions = ({
    isRenderedInMilestone,
    templateState,
    template,
    changes,
    setView,
    setActiveSubtaskId,
    setFiles,
    setSharedUrls,
    setChanges,
    setDrawerState,
    setConfirmationModal,
    closeConfirmationModal,
    setScheduleModal,
    save,
    publish,
    unpublish,
    unschedule,
    deleteTemplate,
}) => {
    const [shouldNotify, setShouldNotify] = useState(false);

    const publishTemplate = () => {
        setConfirmationModal({
            visible: true,
            destructive: false,
            title: 'Publish Task Now?',
            content: 'The task will become visible to other users.',
            confirmText: 'Publish',
            onConfirm: () => {
                closeConfirmationModal();
                publish(template.id, changes);
            },
            onCancel: closeConfirmationModal,
        });
    };

    const unpublishTemplate = () => {
        setConfirmationModal({
            visible: true,
            destructive: true,
            title: 'Unpublish Task?',
            content: `The task will be removed from each team's taskboard.`,
            confirmText: 'Unpublish',
            onConfirm: () => {
                closeConfirmationModal();
                unpublish(template.id);
            },
            onCancel: closeConfirmationModal,
        });
    };

    const unscheduleTemplate = () => {
        setConfirmationModal({
            visible: true,
            destructive: false,
            title: 'Unschedule Task?',
            content: `The task will be unscheduled and reverted to a draft.`,
            confirmText: 'Unschedule',
            onConfirm: () => {
                closeConfirmationModal();
                unschedule(template.id);
            },
            onCancel: closeConfirmationModal,
        });
    };

    const _deleteTemplate = () => {
        setConfirmationModal({
            visible: true,
            destructive: true,
            title: 'Delete Task?',
            content: 'This template will be permanently deleted.',
            confirmText: 'Delete',
            onConfirm: () => {
                closeConfirmationModal();
                setDrawerState((current) => ({ ...current, isOpen: false }));
                deleteTemplate(template.id);
            },
            onCancel: closeConfirmationModal,
        });
    };

    const undoResourcesChanges = (changes) => {
        const { resources } = changes;
        if (!resources) return;
        resources.forEach((resource) => {
            switch (resource.type) {
                case 'file':
                    setFiles((current) => {
                        return {
                            ...current,
                            [resource.id]: { ...current[resource.id], active: resource.action === 'delete' },
                        };
                    });
                    break;
                case 'link':
                    setSharedUrls((current) => {
                        return {
                            ...current,
                            [resource.id]: { ...current[resource.id], active: resource.action === 'delete' },
                        };
                    });
                    break;
                default:
                    break;
            }
        });
    };

    const handleClickSplitButton = ({ key }) => {
        switch (key) {
            case 'publish':
                publishTemplate();
                break;
            case 'save':
                if (template.isLocal) {
                    save(template.id);
                } else {
                    if (templateState === 'draft' || templateState === 'scheduled') {
                        save(template.id, changes);
                        setView('task');
                        setActiveSubtaskId('');
                    }
                    if (templateState === 'published') {
                        save(template.id, changes, shouldNotify);
                        setView('task');
                        setActiveSubtaskId('');
                    }
                }
                break;
            case 'schedule':
                setScheduleModal({ visible: true });
                break;
            case 'unschedule':
                unscheduleTemplate();
                break;
        }
    };

    const getSplitButtonOptions = () => {
        if (template.isLocal) {
            return [
                { key: 'publish', value: 'Publish' },
                { key: 'schedule', value: 'Schedule' },
                { key: 'save', value: 'Save as Draft' },
            ];
        }
        if (templateState === 'draft') {
            if (Object.keys(omit(changes, ['published_on'])).length) {
                return [
                    { key: 'save', value: 'Save Changes' },
                    { key: 'publish', value: 'Save & Publish' },
                    { key: 'schedule', value: 'Schedule' },
                ];
            }
            return [
                { key: 'publish', value: 'Publish' },
                { key: 'schedule', value: 'Schedule' },
            ];
        }
        if (templateState === 'scheduled') {
            if (Object.keys(omit(changes, ['published_on'])).length) {
                return [
                    { key: 'save', value: 'Save Changes' },
                    { key: 'unschedule', value: 'Unschedule' },
                    { key: 'schedule', value: 'Reschedule' },
                    { key: 'publish', value: 'Publish' },
                ];
            }
            return [
                { key: 'unschedule', value: 'Unschedule' },
                { key: 'schedule', value: 'Reschedule' },
                { key: 'publish', value: 'Publish' },
            ];
        }
    };

    const hasChanges = Boolean(Object.keys(omit(changes, ['published_on'])).length);

    let isActive = false;
    if (template.isLocal) {
        isActive = Boolean(template.title);
    } else {
        if (isRenderedInMilestone) {
            isActive = hasChanges;
        } else {
            isActive = templateState === 'published' ? hasChanges : true;
        }
        isActive = Object.keys(changes).includes('title') ? Boolean(changes.title) : isActive;
    }

    const isDisabled = !isActive;

    return (
        <Stack
            sx={{ width: '100%' }}
            alignItems={'center'}
            direction={'row-reverse'}
            justifyContent={'space-between'}
            spacing={4}
        >
            {isRenderedInMilestone ? (
                <Button
                    data-cy={'drawer-save-button'}
                    disabled={isDisabled}
                    onClick={() => handleClickSplitButton({ key: 'save' })}
                >
                    {template.isLocal ? 'Save' : 'Save Changes'}
                </Button>
            ) : (
                <>
                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                        {hasChanges && (
                            <Button
                                variant={'secondary'}
                                onClick={() => {
                                    setView('task');
                                    setActiveSubtaskId('');
                                    undoResourcesChanges(changes);
                                    setChanges({});
                                }}
                            >
                                Cancel
                            </Button>
                        )}
                        {templateState === 'published' ? (
                            <Button disabled={isDisabled} onClick={() => handleClickSplitButton({ key: 'save' })}>
                                Save
                            </Button>
                        ) : (
                            <SplitButton options={getSplitButtonOptions()} onClick={handleClickSplitButton} />
                        )}
                        <Dropdown
                            id={'template-actions-dropdown'}
                            options={
                                templateState === 'published'
                                    ? [
                                          { key: 'unpublish', value: 'Unpublish' },
                                          { key: 'delete', value: 'Unpublish & Delete' },
                                      ]
                                    : [{ key: 'delete', value: 'Delete' }]
                            }
                            rootComponent={
                                <IconButton
                                    aria-label={'more options'}
                                    kind={'moreHorizontal'}
                                    size={'medium'}
                                    variant={'square'}
                                />
                            }
                            onSelect={({ key }) => {
                                switch (key) {
                                    case 'unpublish':
                                        return unpublishTemplate();
                                    case 'delete':
                                        return _deleteTemplate();
                                }
                            }}
                        />
                    </Stack>
                    {templateState === 'published' && hasChanges ? (
                        <CustomCheckbox
                            checked={shouldNotify}
                            label={'Notify users of changes'}
                            onChange={() => setShouldNotify((current) => !current)}
                        />
                    ) : null}
                </>
            )}
        </Stack>
    );
};

const CustomCheckbox = styled(Checkbox)(() => {
    return {
        '& > .MuiTypography-root': {
            whiteSpace: 'unset',
        },
    };
});

export default DrawerActions;
